import { ChevronRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type { NavigationItem } from "../../types";

interface NavigationSectionProps {
  title: string;
  items: NavigationItem[];
  onNavigation: (path: string) => void;
}

export function NavigationSection({ title, items, onNavigation }: NavigationSectionProps) {
  if (items.length === 0) return null;

  return (
    <div>
      <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-4">
        {title}
      </h3>
      <div className="grid grid-cols-1 gap-3">
        {items.map((item) => {
          const Icon = item.icon;
          const isPrimary = item.variant === "primary";

          return (
            <Button
              key={item.key}
              variant="ghost"
              onClick={() => onNavigation(item.path)}
              className="h-auto p-2 justify-start hover:bg-muted/50 group"
            >
              <div className="flex items-center space-x-4 w-full">
                <div className="flex-shrink-0">
                  <div
                    className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${
                      isPrimary
                        ? "bg-indigo-100 dark:bg-indigo-900/20 group-hover:bg-indigo-200 dark:group-hover:bg-indigo-800/30"
                        : "bg-gray-100 dark:bg-gray-800/50 group-hover:bg-gray-200 dark:group-hover:bg-gray-700/50"
                    }`}
                  >
                    <Icon
                      className={`w-4 h-4 ${
                        isPrimary
                          ? "text-indigo-600 dark:text-indigo-400"
                          : "text-gray-600 dark:text-gray-400"
                      }`}
                    />
                  </div>
                </div>
                <div className="flex-1 text-left min-w-0 flex items-center">
                  <p className="text-sm font-medium text-foreground truncate">{item.label}</p>
                </div>
                <div className="flex items-center space-x-2">
                  {item.badge && (
                    <Badge variant="destructive" className="px-2 py-0.5 text-xs">
                      {Number(item.badge) > 99 ? "99+" : item.badge}
                    </Badge>
                  )}
                  <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground" />
                </div>
              </div>
            </Button>
          );
        })}
      </div>
    </div>
  );
}
